import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import Logo from "../../../Images/NucleusTeq Logo.png";
import { useSelector } from "react-redux";
import { view_resume } from "../../../Api/apiService";
import { useLocation, useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import PizZip from "pizzip";
import { Empty } from 'antd';


const ViewResumeDashboard = forwardRef(({ userId, profileId }, ref) => {  
    useImperativeHandle(ref, () => ({
    downloadPDF,
    downloadWord
  }));

   const downloadPDF = () => {
    const element = document.getElementById("resume-content");
    const opt = {
      margin: [10, 0, 23, 0],
      filename: `${profile.profileName}_Resume.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2, scrollX: 0, scrollY: 0, useCORS: true },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
      pagebreak: {
        mode: ["css", "legacy"],
        avoid: ".section-title", 
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  const downloadWord = () => {
        fetch("/resume_template.docx") 
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to load the Word template file.");
            }
            return response.arrayBuffer();
          })
          .then((content) => {
            // Use PizZip to load the content
            const zip = new PizZip(content);
            const doc = new Docxtemplater(zip, {
              paragraphLoop: true,
              linebreaks: true,
            });
    
            // Replace placeholders in the template with dynamic data
            doc.render({
              name: profile?.profileName || "Employee",
              objective: profile?.objective || "N/A",
              professionalSummary: profile?.profileData?.professionalSummary || "N/A",
              technicalSkills: profile?.profileData?.technicalSkills || [],
              professionalExperience: profile?.profileData?.professionalExperience || [],
              education: profile?.profileData?.education || [],
              certificates: profile?.profileData?.certificates || [],
            });
    
            // Generate the Word document as a blob
            const blob = doc.getZip().generate({
              type: "blob",
              mimeType:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
    
            console.log("Docxtemplater Data:", doc.getFullText());
    
            // Save the Word document
            saveAs(blob, `${profile?.profileName || "Employee"}_Resume.docx`);
          })
          .catch((error) => {
            console.error("Error loading or generating Word template:", error);
          });
          
      };
  

  

  const role = useSelector((state) => state.auth);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await view_resume(userId);

        const selectedProfile = response.data.find((profile) => profile.id === parseInt(profileId));

        if (selectedProfile) {
          const email = JSON.parse(localStorage.getItem("auth"))?.email;
          setProfile({ ...selectedProfile, email });
        } else {
          setError("Profile not found");
        }
      } catch (error) {
        setError("Error fetching profile data.");
      } finally {
        setLoading(false);
      }
    };

    if (userId && profileId) {
      fetchProfile();
    }
  }, [userId, profileId]);

  const formatExperienceDetails = (details) => {
    if (!details) return null;

    return details
      .split("\n") // Split by newline instead of period
      .map((line, index) => {
        let trimmedLine = line.trim();

        // Remove bullet point if it exists at the start
        if (trimmedLine.startsWith("•")) {
          trimmedLine = trimmedLine.substring(1).trim();
        }

        // Skip empty lines
        if (!trimmedLine) return null;

        return <li key={index}>{trimmedLine}</li>;
      })
      .filter(Boolean); // Remove null entries
  };

  const formatDate = (date) => {
    if (!date) return "";

    if (date.toLowerCase() === "present" || date.toLowerCase() === "current" || date.toLowerCase() === "now" || date.toLowerCase() === "till date") {
      return "Present";
    }

    const monthYearRegex = /^([a-zA-Z]{3,9})['’]?(\d{2}|\d{4})$/;
    const match = date.match(monthYearRegex);

    if (match) {
      const month = match[1];
      const year = `20${match[2]}`;
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      const options = { year: "numeric", month: "2-digit" };
      return new Date(year, monthIndex, 1).toLocaleDateString("en-GB", options).replace(/\//g, "/");
    }

    const numericMonthYearRegex = /^(\d{1,2})\/(\d{4})$/;
    const numericMatch = date.match(numericMonthYearRegex);

    if (numericMatch) {
      const month = numericMatch[1];
      const year = numericMatch[2];
      const options = { year: "numeric", month: "2-digit" };
      return new Date(year, month - 1, 1).toLocaleDateString("en-GB", options).replace(/\//g, "/");
    }

    const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const isoMatch = date.match(isoDateRegex);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1;
      const day = parseInt(isoMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const usDateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const usMatch = date.match(usDateRegex);
    if (usMatch) {
      const month = parseInt(usMatch[1], 10) - 1;
      const day = parseInt(usMatch[2], 10);
      const year = parseInt(usMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const euDateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const euMatch = date.match(euDateRegex);
    if (euMatch) {
      const day = parseInt(euMatch[1], 10);
      const month = parseInt(euMatch[2], 10) - 1;
      const year = parseInt(euMatch[3], 10);
      return new Date(year, month, day).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }

    const fullMonthYearRegex = /^([a-zA-Z]{3,9}) (\d{4})$/;
    const fullMonthYearMatch = date.match(fullMonthYearRegex);
    if (fullMonthYearMatch) {
      const month = fullMonthYearMatch[1];
      const year = parseInt(fullMonthYearMatch[2], 10);
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      return new Date(year, monthIndex, 1).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
      });
    }

    return date;
  };

 if (loading) return (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Loading..." />
  </div>
);

if (error) return (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
  </div>
);

if (!profile) return (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No profile data available" />
  </div>
);

  const sortedExperience = [...(profile.profileData?.professionalExperience || [])].sort((a, b) => new Date(b.endDate) - new Date(a.endDate));

  const sortedEducation = [...(profile.profileData?.education || [])].sort(
    (a, b) => new Date(b.duration.split(" - ")[1]) - new Date(a.duration.split(" - ")[1])
  );

  return (
    <div>
      <div className="resume-container" id="resume-content">
        <div className="logo-container">
          <img src={Logo} alt="logo" className="logo" />
        </div>

        <div className="personal-info">
          <h1>{role.role === "ROLE_EMPLOYEE" ? role.name : profile.profileName}</h1>
        </div>

        {profile.objective && (
          <div className="sub-section element">
            <h2 className="section-title element">Objective</h2>
            <div className="resume-block">
              <p>{profile.objective}</p>
            </div>
          </div>
        )}

        {profile.profileData?.professionalSummary && (
          <div className="sub-section element">
            <h2 className="section-title element">Professional Summary</h2>
            <div className="resume-block">
              <p className="section-para element">{profile.profileData.professionalSummary}</p>
            </div>
          </div>
        )}

        {profile.profileData?.technicalSkills && (
          <>
            <h2 className="section-title element">Technical Skills</h2>

            {profile.profileData.technicalSkills.technology?.length > 0 && (
              <div className="sub-section element">
                <h3 className="sub-section-title element">Technology</h3>
                <ul className="bullet-section-tech element">
                  {profile.profileData.technicalSkills.technology.map((tech, index) => (
                    <div key={index} className="resume-block">
                      <li>{tech}</li>
                    </div>
                  ))}
                </ul>
              </div>
            )}

            {profile.profileData.technicalSkills.programming?.length > 0 && (
              <div className="sub-section element">
                <h3 className="sub-section-title element">Programming</h3>
                <ul className="bullet-section-tech element">
                  {profile.profileData.technicalSkills.programming.map((prog, index) => (
                    <div key={index} className="resume-block">
                      <li>{prog}</li>
                    </div>
                  ))}
                </ul>
              </div>
            )}

            {profile.profileData.technicalSkills.tools?.length > 0 && (
              <div className="sub-section element">
                <h3 className="sub-section-title element">Tools</h3>
                <ul className="bullet-section-tech element">
                  {profile.profileData.technicalSkills.tools.map((tool, index) => (
                    <div key={index} className="resume-block">
                      <li>{tool}</li>
                    </div>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {sortedExperience.length > 0 && (
          <div className="sub-section element">
            <h2 className="section-title element">Professional Experience</h2>
            {sortedExperience.map((experience, index) => (
              <div key={index} className="professional-experience element">
                <h3 className="experience-heading element">
                  {experience.jobTitle} at {experience.companyName}
                  <span className="experience-dates element">
                    ({experience.startDate} - {experience.endDate})
                  </span>
                </h3>

                <div className="resume-block">
                  <p>
                    <strong>Project:</strong> {experience.projectName}
                  </p>
                </div>

                <div className="resume-block">
                  <p>
                    <strong>Technologies Used:</strong> {experience.techStack}
                  </p>
                </div>

                <ul className="bullet-section element">
                  {experience.details && formatExperienceDetails(experience.details).map((point, idx) => (
                    <div key={idx} className="resume-block">
                      <li>{point}</li>
                    </div>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {sortedEducation.length > 0 && (
          <div className="sub-section element">
            <h2 className="section-title element">Education</h2>
            {sortedEducation.map((education, index) => (
              <div key={index} className="education element">
                <h3 className="experience-heading element">
                  {education.course} from {education.collegeName}
                </h3>
                <div className="resume-block">
                  <p className="element">
                    Duration: {formatDate(education.duration.split(" - ")[0])} - {formatDate(education.duration.split(" - ")[1])}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        
              {profile.profileData?.certificates?.length > 0 && (
                <div>
                  <h2 className="section-title">Certificates</h2>
                  <ul className="bullet-section">
                    {profile.profileData.certificates.map((certificate, index) => (
                      <li key={index}>{certificate}</li>
                    ))}
                  </ul>
                </div>
              )}
        
      </div>
    </div>
  );
});

export default ViewResumeDashboard;
